// Personalizacao > Marca d'agua: liga/desliga, galeria dos 5 modelos, editor
// visual com previa real (renderizada pelo bot), logo e onde aplicar.
// Gate PRO via entitlements (hasWatermark).

import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { AlertCircle, CheckCircle2, Loader2, Lock, Save, Stamp } from "lucide-react";
import type { WatermarkPresetId, WatermarkSettings } from "@lucralink/shared";
import { Button } from "@/components/ui/button";
import { InlineError, Toggle } from "@/components/forwarding/controls";
import { useTenant } from "@/lib/hooks/useTenant";
import { useEntitlements } from "@/lib/entitlements";
import { useWatermarkSettings, type PreviewState } from "@/lib/hooks/useWatermark";
import { WatermarkCanvas } from "./WatermarkCanvas";
import { WatermarkInspector } from "./WatermarkInspector";
import { FlowsCard, LogoCard, PresetGallery } from "./WatermarkParts";
import { useWatermarkEditor, type Feedback } from "./useWatermarkEditor";

function Card({
  title,
  children,
  aside,
}: {
  title: string;
  children: ReactNode;
  aside?: ReactNode;
}): JSX.Element {
  return (
    <section className="overflow-hidden rounded-xl border border-border bg-card">
      <div className="flex items-center justify-between gap-2.5 border-b border-border bg-[hsl(var(--surface-raised)/0.4)] px-5 py-3">
        <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          {title}
        </h3>
        {aside}
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

function ProUpsell(): JSX.Element {
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 rounded-lg border border-primary/25 bg-primary/5 p-4">
        <Lock size={16} className="mt-0.5 shrink-0 text-primary" />
        <div className="text-sm">
          <p className="font-medium">Marca d'água é do plano PRO</p>
          <p className="mt-0.5 text-muted-foreground">
            Sua marca, preço e selo de desconto em todas as fotos enviadas, com prévia exata do que
            sai.{" "}
            <Link to="/app/plans" className="text-primary hover:underline">
              Ver planos
            </Link>
          </p>
        </div>
      </div>
      <PresetGallery selected={null} onPick={() => undefined} disabled />
    </div>
  );
}

function SectionShell({ children }: { children: ReactNode }): JSX.Element {
  return (
    <section aria-labelledby="section-watermark" className="space-y-4">
      <div className="flex items-center gap-2">
        <Stamp size={16} className="text-muted-foreground" />
        <h2 id="section-watermark" className="text-base font-semibold">
          Marca d'água
        </h2>
      </div>
      {children}
    </section>
  );
}

export function WatermarkSection(): JSX.Element {
  const { data: billing } = useTenant();
  const tenant = billing?.tenant ?? null;
  const entitlements = useEntitlements(tenant);
  const settingsQuery = useWatermarkSettings(entitlements.hasWatermark);

  if (!entitlements.hasWatermark) {
    return (
      <SectionShell>
        <ProUpsell />
      </SectionShell>
    );
  }
  if (!settingsQuery.data) {
    return (
      <SectionShell>
        {settingsQuery.isError ? (
          <InlineError message="Não foi possível carregar a marca d'água." />
        ) : (
          <div className="h-64 animate-pulse rounded-xl bg-muted/60" />
        )}
      </SectionShell>
    );
  }
  return (
    <SectionShell>
      <WatermarkEditor tenantId={tenant?.id ?? null} settings={settingsQuery.data} />
    </SectionShell>
  );
}

function PresetConfirm({
  onKeep,
  onSwitch,
}: {
  onKeep: () => void;
  onSwitch: () => void;
}): JSX.Element {
  return (
    <div
      role="alertdialog"
      aria-label="Trocar de modelo"
      className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs"
    >
      <span>Trocar de modelo descarta as edições não salvas deste desenho.</span>
      <span className="flex gap-2">
        <Button type="button" size="sm" variant="ghost" onClick={onKeep}>
          Manter edições
        </Button>
        <Button type="button" size="sm" onClick={onSwitch}>
          Trocar mesmo assim
        </Button>
      </span>
    </div>
  );
}

function FeedbackBanner({ feedback }: { feedback: Feedback }): JSX.Element | null {
  if (!feedback) return null;
  const ok = feedback.kind === "ok";
  return (
    <div
      role="status"
      className={
        ok
          ? "flex items-center gap-2 rounded-lg border border-primary/25 bg-primary/10 px-4 py-3 text-sm text-primary"
          : "flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
      }
    >
      {ok ? <CheckCircle2 size={15} /> : <AlertCircle size={15} />}
      <span>{feedback.text}</span>
    </div>
  );
}

function SaveBar({
  dirty,
  saving,
  onSave,
}: {
  dirty: boolean;
  saving: boolean;
  onSave: () => void;
}): JSX.Element {
  return (
    <div className="sticky bottom-3 z-10 flex items-center justify-end gap-3 rounded-lg border border-border bg-card/95 px-4 py-2.5 shadow-sm backdrop-blur">
      {dirty && <span className="text-xs text-muted-foreground">Alterações não salvas</span>}
      <Button type="button" onClick={onSave} disabled={saving || !dirty}>
        {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
        Salvar marca d'água
      </Button>
    </div>
  );
}

function PreviewStatusNote({ preview }: { preview: PreviewState }): JSX.Element {
  return preview.error ? (
    <span className="text-[11px] text-destructive">{preview.error}</span>
  ) : (
    <span className="text-[11px] text-muted-foreground">
      Prévia gerada pelo mesmo motor do envio
    </span>
  );
}

type Editor = ReturnType<typeof useWatermarkEditor>;

function EditorCard({ editor: e }: { editor: Editor }): JSX.Element {
  const setDesign = (design: Editor["draft"]["design"]) => e.patch({ design });
  const common = {
    design: e.draft.design,
    selectedId: e.selectedId,
    onSelect: e.setSelectedId,
    onChange: setDesign,
  };
  return (
    <Card title="Editor" aside={<PreviewStatusNote preview={e.preview} />}>
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
        <WatermarkCanvas {...common} preview={e.preview} />
        <WatermarkInspector {...common} />
      </div>
    </Card>
  );
}

function WatermarkEditor({
  tenantId,
  settings,
}: {
  tenantId: string | null;
  settings: WatermarkSettings;
}): JSX.Element {
  const e = useWatermarkEditor(tenantId, settings);
  const pending: WatermarkPresetId | null = e.pendingPreset;
  return (
    <div className="space-y-4">
      <Card title="Ativação">
        <Toggle
          id="watermark-enabled"
          checked={e.draft.enabled}
          onChange={(enabled) => e.patch({ enabled })}
          label="Marca d'água ativa"
          hint="Aplicada antes do envio no Telegram e no WhatsApp. Se a imagem falhar, a oferta sai sem marca, nunca deixa de sair."
        />
      </Card>
      <Card title="Modelos">
        {pending && (
          <PresetConfirm onKeep={e.cancelPreset} onSwitch={() => e.applyPreset(pending)} />
        )}
        <PresetGallery selected={e.draft.design.preset} onPick={e.pickPreset} />
      </Card>
      <EditorCard editor={e} />
      <Card title="Logo">
        <LogoCard
          logoUrl={e.logo.logoUrl}
          busy={e.logo.busy}
          onUpload={e.logo.upload}
          onRemove={e.logo.remove}
        />
      </Card>
      <Card title="Onde aplicar">
        <FlowsCard
          applyTo={e.draft.applyTo}
          flows={e.draft.flows}
          onApplyTo={(applyTo) => e.patch({ applyTo })}
          onFlows={(flows) => e.patch({ flows })}
        />
      </Card>
      <FeedbackBanner feedback={e.feedback} />
      <SaveBar dirty={e.dirty} saving={e.saving} onSave={e.onSave} />
    </div>
  );
}
